import { findModelConfig } from "@/client/config/models.config";
import type { Task } from "@/client/hooks/useTaskManager";
import { isRecord } from "@/client/utils/typeGuards";

const SORA_MODEL_KEYS = new Set(["sora-2-async", "sora-2-async-remix"]);

const normalizeModelId = (identifier?: string | null): string => {
  if (typeof identifier !== "string") {
    return "";
  }
  return identifier.trim().toLowerCase();
};

const isModelIdentifierInSet = (identifier: string | null | undefined, candidates: Set<string>): boolean => {
  const normalized = normalizeModelId(identifier);
  if (!normalized) {
    return false;
  }
  if (candidates.has(normalized)) {
    return true;
  }
  const config = findModelConfig(normalized);
  if (!config) {
    return false;
  }
  const configKey = normalizeModelId(config.modelKey);
  return candidates.has(configKey);
};

const isSoraModelIdentifier = (identifier?: string | null): boolean => {
  return isModelIdentifierInSet(identifier ?? null, SORA_MODEL_KEYS);
};

export const isSoraTask = (task?: Task | null): boolean => {
  if (!task) {
    return false;
  }
  return isSoraModelIdentifier(task.model);
};

const VIDEO_ID_PREFIXES = ["video_", "vd_", "soravideo_", "vid_"];

const isLikelyVideoId = (value: string): boolean => {
  const normalized = value.trim();
  if (!normalized) {
    return false;
  }
  const lower = normalized.toLowerCase();
  if (VIDEO_ID_PREFIXES.some((prefix) => lower.startsWith(prefix))) {
    return true;
  }
  // OpenAI Sora video ids resemble UUIDs prefixed with "video_"
  return /^[a-z0-9_-]{8,}$/i.test(normalized);
};

const pickString = (value: unknown): string | undefined => {
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (trimmed) {
      return trimmed;
    }
  }
  return undefined;
};

const candidateKeys = [
  "id",
  "video_id",
  "videoId",
  "request_id",
  "requestId",
  "detail_id",
  "detailId",
  "video",
  "video_url",
  "videoUrl",
  "remixed_from_video_id",
  "original_video_id",
  "data_id",
  "dataId",
];

const searchVideoId = (value: unknown, depth = 0): string | undefined => {
  if (!value) return undefined;
  if (depth > 5) return undefined;

  if (typeof value === "string") {
    const candidate = pickString(value);
    if (candidate && isLikelyVideoId(candidate)) {
      return candidate;
    }
    return undefined;
  }

  if (Array.isArray(value)) {
    for (const item of value) {
      const found = searchVideoId(item, depth + 1);
      if (found) return found;
    }
    return undefined;
  }

  if (isRecord(value)) {
    for (const key of candidateKeys) {
      if (key in value) {
        const candidate = pickString(value[key]);
        if (candidate && isLikelyVideoId(candidate)) {
          return candidate;
        }
      }
    }
    for (const nested of Object.values(value)) {
      const found = searchVideoId(nested, depth + 1);
      if (found) return found;
    }
  }

  return undefined;
};

export const getSoraVideoId = (task?: Task | null): string | undefined => {
  if (!task || !isSoraTask(task)) {
    return undefined;
  }

  const requestId = pickString(task.requestId);
  if (requestId) {
    return requestId;
  }

  const payload = task.payload;
  if (payload && typeof payload === "object") {
    const fromPayload = searchVideoId(payload);
    if (fromPayload) {
      return fromPayload;
    }
  }

  const result = task.result;
  if (result && typeof result === "object") {
    const fromResult = searchVideoId(result);
    if (fromResult) {
      return fromResult;
    }
  }

  return undefined;
};
